import { COUNTRY } from './country';
import { EnumHelperBase, type EnumDataObject } from './enum.helpers';

export enum CZ_REGION {
	PRAGUE = 1,
	CENTRAL_BOHEMIAN = 2,
	SOUTH_BOHEMIAN = 3,
	PLZEN = 4,
	KARLOVY_VARY = 5,
	USTI_NAD_LABEM = 6,
	LIBEREC = 7,
	HRADEC_KRALOVE = 8,
	PARDUBICE = 9,
	VYSOCINA = 10,
	SOUTH_MORAVIAN = 11,
	OLOMOUC = 12,
	ZLIN = 13,
	MORAVIAN_SILESIAN = 14,
}

export enum SK_REGION {
	BRATISLAVA = 201,
	TRNAVA = 202,
	TRENCIN = 203,
	NITRA = 204,
	ZILINA = 205,
	BANSKA_BYSTRICA = 206,
	PRESOV = 207,
	KOSICE = 208,
}

export enum AU_REGION {
	VIENNA = 301,
	LOWER_AUSTRIA = 302,
	UPPER_AUSTRIA = 303,
	STYRIA = 304,
	CARINTHIA = 305,
	BURGENLAND = 306,
	SALZBURG = 307,
	TYROLIA = 308,
	VORARLBERG = 309,
}

export enum RS_REGION {
	VOJVODINA = 501,
	BELGRADE = 502,
	KOSOVO = 503,
	METOHIJA = 504,
	SUMADIJA_AND_WESTERN_SERBIA = 505,
	SOUTHERN_AND_EASTERN_SERBIA = 506,
}

export enum GR_REGION {
	ATTICA = 1501,
	CENTRAL_GREECE = 1502,
	CENTRAL_MACEDONIA = 1503,
	CRETE = 1504,
	EASTERN_MACEDONIA_AND_THRACE = 1505,
	EPIRUS = 1506,
	IONIAN_ISLANDS = 1507,
	NORTH_AEGEAN = 1508,
	PELOPONNESE = 1509,
	SOUTH_AEGEAN = 1510,
	THESSALY = 1511,
	WESTERN_GREECE = 1512,
	WESTERN_MACEDONIA = 1513,
}

export enum DE_REGION {
	BADEN_WUERTTEMBERG = 1401,
	BAVARIA = 1402,
	BERLIN = 1403,
	BRANDENBURG = 1404,
	BREMEN = 1405,
	HAMBURG = 1406,
	HESSE = 1407,
	LOWER_SAXONY = 1408,
	MECKLENBURG_VORPOMMERN = 1409,
	NORTH_RHINE_WESTPHALIA = 1410,
	RHINELAND_PALATINATE = 1411,
	SAARLAND = 1412,
	SAXONY = 1413,
	SAXONY_ANHALT = 1414,
	SCHLESWIG_HOLSTEIN = 1415,
	THURINGIA = 1416,
}

export type REGION = CZ_REGION | SK_REGION | AU_REGION | RS_REGION | GR_REGION | DE_REGION;

export interface RegionEnumBaseData extends EnumDataObject<RegionEnumBaseExtraData, REGION, 'regionId'> {}

export interface RegionEnumBaseExtraData {
	countryId: COUNTRY;
}

/**
 * Base Region enumeration
 * @see https://github.com/NFCtron/nfctron-db/blob/dev/diagrams/global.png
 */
class RegionEnumBase extends EnumHelperBase<RegionEnumBaseExtraData, REGION, 'region', 'regionId'> {
	constructor() {
		super(
			'region',
			{
				[CZ_REGION.PRAGUE]: { countryId: COUNTRY.CZE },
				[CZ_REGION.CENTRAL_BOHEMIAN]: { countryId: COUNTRY.CZE },
				[CZ_REGION.SOUTH_BOHEMIAN]: { countryId: COUNTRY.CZE },
				[CZ_REGION.PLZEN]: { countryId: COUNTRY.CZE },
				[CZ_REGION.KARLOVY_VARY]: { countryId: COUNTRY.CZE },
				[CZ_REGION.USTI_NAD_LABEM]: { countryId: COUNTRY.CZE },
				[CZ_REGION.LIBEREC]: { countryId: COUNTRY.CZE },
				[CZ_REGION.HRADEC_KRALOVE]: { countryId: COUNTRY.CZE },
				[CZ_REGION.PARDUBICE]: { countryId: COUNTRY.CZE },
				[CZ_REGION.VYSOCINA]: { countryId: COUNTRY.CZE },
				[CZ_REGION.SOUTH_MORAVIAN]: { countryId: COUNTRY.CZE },
				[CZ_REGION.OLOMOUC]: { countryId: COUNTRY.CZE },
				[CZ_REGION.ZLIN]: { countryId: COUNTRY.CZE },
				[CZ_REGION.MORAVIAN_SILESIAN]: { countryId: COUNTRY.CZE },
				[SK_REGION.BRATISLAVA]: { countryId: COUNTRY.SVK },
				[SK_REGION.TRNAVA]: { countryId: COUNTRY.SVK },
				[SK_REGION.TRENCIN]: { countryId: COUNTRY.SVK },
				[SK_REGION.NITRA]: { countryId: COUNTRY.SVK },
				[SK_REGION.ZILINA]: { countryId: COUNTRY.SVK },
				[SK_REGION.BANSKA_BYSTRICA]: { countryId: COUNTRY.SVK },
				[SK_REGION.PRESOV]: { countryId: COUNTRY.SVK },
				[SK_REGION.KOSICE]: { countryId: COUNTRY.SVK },
				[AU_REGION.VIENNA]: { countryId: COUNTRY.AUT },
				[AU_REGION.LOWER_AUSTRIA]: { countryId: COUNTRY.AUT },
				[AU_REGION.UPPER_AUSTRIA]: { countryId: COUNTRY.AUT },
				[AU_REGION.STYRIA]: { countryId: COUNTRY.AUT },
				[AU_REGION.CARINTHIA]: { countryId: COUNTRY.AUT },
				[AU_REGION.BURGENLAND]: { countryId: COUNTRY.AUT },
				[AU_REGION.SALZBURG]: { countryId: COUNTRY.AUT },
				[AU_REGION.TYROLIA]: { countryId: COUNTRY.AUT },
				[AU_REGION.VORARLBERG]: { countryId: COUNTRY.AUT },
				[RS_REGION.VOJVODINA]: { countryId: COUNTRY.SRB },
				[RS_REGION.BELGRADE]: { countryId: COUNTRY.SRB },
				[RS_REGION.KOSOVO]: { countryId: COUNTRY.SRB },
				[RS_REGION.METOHIJA]: { countryId: COUNTRY.SRB },
				[RS_REGION.SUMADIJA_AND_WESTERN_SERBIA]: { countryId: COUNTRY.SRB },
				[RS_REGION.SOUTHERN_AND_EASTERN_SERBIA]: { countryId: COUNTRY.SRB },
				[GR_REGION.ATTICA]: { countryId: COUNTRY.GRC },
				[GR_REGION.CENTRAL_GREECE]: { countryId: COUNTRY.GRC },
				[GR_REGION.CENTRAL_MACEDONIA]: { countryId: COUNTRY.GRC },
				[GR_REGION.CRETE]: { countryId: COUNTRY.GRC },
				[GR_REGION.EASTERN_MACEDONIA_AND_THRACE]: { countryId: COUNTRY.GRC },
				[GR_REGION.EPIRUS]: { countryId: COUNTRY.GRC },
				[GR_REGION.IONIAN_ISLANDS]: { countryId: COUNTRY.GRC },
				[GR_REGION.NORTH_AEGEAN]: { countryId: COUNTRY.GRC },
				[GR_REGION.PELOPONNESE]: { countryId: COUNTRY.GRC },
				[GR_REGION.SOUTH_AEGEAN]: { countryId: COUNTRY.GRC },
				[GR_REGION.THESSALY]: { countryId: COUNTRY.GRC },
				[GR_REGION.WESTERN_GREECE]: { countryId: COUNTRY.GRC },
				[GR_REGION.WESTERN_MACEDONIA]: { countryId: COUNTRY.GRC },
				[DE_REGION.SAXONY_ANHALT]: { countryId: COUNTRY.DEU },
				[DE_REGION.BADEN_WUERTTEMBERG]: { countryId: COUNTRY.DEU },
				[DE_REGION.BAVARIA]: { countryId: COUNTRY.DEU },
				[DE_REGION.BERLIN]: { countryId: COUNTRY.DEU },
				[DE_REGION.BRANDENBURG]: { countryId: COUNTRY.DEU },
				[DE_REGION.BREMEN]: { countryId: COUNTRY.DEU },
				[DE_REGION.HAMBURG]: { countryId: COUNTRY.DEU },
				[DE_REGION.HESSE]: { countryId: COUNTRY.DEU },
				[DE_REGION.LOWER_SAXONY]: { countryId: COUNTRY.DEU },
				[DE_REGION.MECKLENBURG_VORPOMMERN]: { countryId: COUNTRY.DEU },
				[DE_REGION.NORTH_RHINE_WESTPHALIA]: { countryId: COUNTRY.DEU },
				[DE_REGION.RHINELAND_PALATINATE]: { countryId: COUNTRY.DEU },
				[DE_REGION.SAARLAND]: { countryId: COUNTRY.DEU },
				[DE_REGION.SAXONY]: { countryId: COUNTRY.DEU },
				[DE_REGION.SCHLESWIG_HOLSTEIN]: { countryId: COUNTRY.DEU },
				[DE_REGION.THURINGIA]: { countryId: COUNTRY.DEU },
			},
			'regionId',
			null,
		);
	}
}

export const regionEnumBase = new RegionEnumBase();
